import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Col, Form, Row, Alert, Spinner } from "react-bootstrap";
import bg2 from "../assets/img/bg02.jpg";
import logo from "../assets/img/logo.jpeg";
import { API_BASE_URL } from "../constants/api";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError("Veuillez saisir votre adresse email");
      return;
    }

    // Validation email simple
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Veuillez saisir une adresse email valide");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/forgot_password.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => navigate("/pages/signin2"), 5000);
      } else {
        setError(data.message || "Une erreur est survenue");
      }
    } catch (err) {
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="forgot-container">
        <div className="container">
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="forgot-card">
                <div className="forgot-header">
                  <div className="logo-wrapper">
                    <img 
                      src={logo} 
                      className="logo-image" 
                      alt="Logo" 
                    />
                  </div>
                  <h3 className="forgot-title">Mot de passe oublié ?</h3>
                  <p className="forgot-subtitle">Ne vous inquiétez pas, nous sommes là pour vous aider</p>
                </div>
                
                <div className="forgot-body">
                  {error && (
                    <Alert variant="danger" onClose={() => setError("")} dismissible className="alert-modern">
                      <i className="ri-error-warning-line me-2"></i>
                      {error}
                    </Alert>
                  )}
                  
                  {success && (
                    <Alert variant="success" className="alert-modern alert-success-custom">
                      <div className="d-flex align-items-start">
                        <i className="ri-mail-check-line fs-4 me-3"></i>
                        <div>
                          <strong>Email envoyé avec succès !</strong>
                          <p className="mb-1 mt-1">
                            Un email de réinitialisation vous a été envoyé. Vérifiez votre boîte de réception.
                          </p>
                          <small className="text-muted">
                            Redirection vers la page de connexion dans 5 secondes...
                          </small>
                        </div>
                      </div>
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label-custom">
                        <i className="ri-mail-line me-2"></i>
                        Adresse Email
                      </Form.Label>
                      <div className="input-icon-wrapper">
                        <i className="ri-mail-line input-icon"></i>
                        <Form.Control
                          type="email"
                          placeholder="exemple@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={loading || success}
                          size="lg"
                          required
                          className="input-with-icon"
                        />
                      </div>
                      <Form.Text className="text-muted small">
                        Nous enverrons un lien de réinitialisation à cette adresse.
                      </Form.Text>
                    </Form.Group>

                    <div className="d-grid gap-3">
                      <Button 
                        type="submit" 
                        className="btn-gradient"
                        disabled={loading || success}
                        size="lg"
                      >
                        {loading ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-line me-2"></i>
                            Envoyer le lien de réinitialisation
                          </>
                        )}
                      </Button>
                      
                      <Button 
                        variant="link" 
                        onClick={() => navigate("/pages/signin2")}
                        className="back-link"
                      >
                        <i className="ri-arrow-left-line me-2"></i>
                        Retour à la connexion
                      </Button>
                    </div>
                  </Form>

                  {/* Informations supplémentaires */}
                  <div className="info-box mt-4">
                    <div className="d-flex gap-3">
                      <div className="info-icon">
                        <i className="ri-shield-check-line"></i>
                      </div>
                      <div className="info-text">
                        <small className="fw-semibold d-block">Sécurité garantie</small>
                        <small className="text-muted">Vos informations sont protégées et resteront confidentielles.</small>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <style>{`
        .forgot-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          padding: 2rem;
        }
        
        .forgot-card {
          border: none;
          border-radius: 32px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }
        
        .forgot-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
          
        }
        
        /* Header styles */
        .forgot-header {
          background: linear-gradient(135deg, #21ACCD 0%, #1a8ba8 100%);
          padding: 2rem;
          text-align: center;
          color: white;
          position: relative;
          overflow: hidden;
        }
        
        .forgot-header::before {
          content: '';
          position: absolute;
          top: -50px;
          right: -50px;
          width: 150px;
          height: 150px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
        }
        
        .forgot-header::after {
          content: '';
          position: absolute;
          bottom: -50px;
          left: -50px;
          width: 200px;
          height: 200px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 50%;
        }
        
        .logo-wrapper {
          margin-bottom: 1.5rem;
          position: relative;
          z-index: 1;
        }
        
        .logo-image {
          height: 70px;
          width: 70px;
          border-radius: 50%;
          border: 3px solid white;
          object-fit: cover;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
          transition: all 0.3s ease;
        }
        
        .logo-image:hover {
          transform: scale(1.05);
        }
        
        .forgot-title {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          position: relative;
          z-index: 1;
        }
        
        .forgot-subtitle {
          opacity: 0.9;
          margin-bottom: 0;
          font-size: 0.9rem;
          position: relative;
          z-index: 1;
        }
        
        /* Body styles */
        .forgot-body {
          padding: 2rem;
          background: white;
        }
        
        /* Form label */
        .form-label-custom {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
          font-size: 0.85rem;
          display: block;
        }
        
        /* Input with icon */
        .input-icon-wrapper {
          position: relative;
          margin-bottom: 0.5rem;
        }
        
        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 1.1rem;
          z-index: 2;
        }
        
        .input-with-icon {
          padding-left: 2.75rem !important;
          border-radius: 14px;
          border: 1.5px solid #e2e8f0;
          transition: all 0.3s ease;
          font-size: 1rem;
        }
        
        .input-with-icon:focus {
          border-color: #21ACCD !important;
          box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
          outline: none;
        }
        
        .input-with-icon:disabled {
          background-color: #f8fafc;
          cursor: not-allowed;
        }
        
        /* Gradient button */
        .btn-gradient {
          background: linear-gradient(135deg, #21ACCD 0%, #1a8ba8 100%);
          border: none;
          padding: 12px 20px;
          font-weight: 600;
          border-radius: 14px;
          transition: all 0.3s ease;
          color: white;
          font-size: 1rem;
        }
        
        .btn-gradient:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(33, 172, 205, 0.3);
          background:  #43A63A !important
        }
        
        .btn-gradient:disabled {
          opacity: 0.7;
          transform: none;
          cursor: not-allowed;
        }
        
        /* Back link */
        .back-link {
          color: #64748b;
          text-decoration: none;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          padding: 0.5rem;
        }
        
        .back-link:hover {
          color: #21ACCD;
          transform: translateX(-4px);
        }
        
        /* Alert modern */
        .alert-modern {
          border: none;
          border-radius: 14px;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .alert-modern.alert-danger {
          background: #fef2f2;
          color: #dc2626;
        }
        
        .alert-success-custom {
          background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
          border-left: 4px solid #10b981;
          border-radius: 14px;
        }
        
        /* Info box */
        .info-box {
          background: #f8fafc !important;
          border-radius: 16px;
          padding: 1rem;
          margin-top: 1.5rem;
        }
        
        .info-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: background:  #43A63A !important;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          color: #21ACCD !important;
          flex-shrink: 0;
        }
        
        .info-text small {
          font-size: 0.75rem;
          line-height: 1.4;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .forgot-container {
            padding: 1rem;
          }
          
          .forgot-body {
            padding: 1.5rem;
          }
          
          .forgot-title {
            font-size: 1.5rem;
          }
          
          .logo-image {
            height: 60px;
            width: 60px;
          }
        }
        
        /* Animation */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .forgot-card {
          animation: fadeInUp 0.5s ease-out;
        }
      `}</style>
    </>
  );
}